/**
 * Відтворення озвучки і запис власної вимови.
 *
 * Основний шлях — заздалегідь згенеровані mp3 з `/audio/<ключ>.mp3`.
 * Якщо файлу немає (не встигли озвучити), працює запасний варіант —
 * вбудований синтез мови браузера з польським голосом.
 */

const cache = new Map<string, HTMLAudioElement>()
let current: HTMLAudioElement | null = null

/** iOS вимагає «розбудити» аудіо у відповідь на дотик користувача. */
let unlocked = false

export function unlockAudio() {
  if (unlocked) return
  const a = new Audio()
  a.src =
    'data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA' +
    'gICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgP////////////////////////////8AAAAA'
  a.volume = 0
  void a.play().catch(() => undefined)
  unlocked = true
}

function element(key: string): HTMLAudioElement {
  let el = cache.get(key)
  if (!el) {
    el = new Audio(`${import.meta.env.BASE_URL}audio/${key}.mp3`)
    el.preload = 'auto'
    cache.set(key, el)
  }
  return el
}

/** Чи є польський голос у системному синтезі — потрібно для запасного шляху. */
function polishVoice(): SpeechSynthesisVoice | undefined {
  if (typeof speechSynthesis === 'undefined') return undefined
  const voices = speechSynthesis.getVoices()
  return voices.find((v) => v.lang?.toLowerCase().startsWith('pl'))
}

function speak(text: string, rate = 0.95) {
  if (typeof speechSynthesis === 'undefined') return
  speechSynthesis.cancel()
  const u = new SpeechSynthesisUtterance(text)
  const v = polishVoice()
  if (v) u.voice = v
  u.lang = 'pl-PL'
  u.rate = rate
  speechSynthesis.speak(u)
}

export interface PlayOptions {
  /** Текст для запасного синтезу, якщо файлу немає. */
  text?: string
  /** Швидкість: 1 — звичайна, 0.7 — повільно, для розбору. */
  rate?: number
}

/** Програє репліку. Повертає проміс, що вирішується після завершення. */
export async function play(key: string | undefined, opts: PlayOptions = {}): Promise<void> {
  stop()
  if (!key) {
    if (opts.text) speak(opts.text, opts.rate)
    return
  }
  const el = element(key)
  el.currentTime = 0
  el.playbackRate = opts.rate ?? 1
  current = el
  try {
    await el.play()
    await new Promise<void>((resolve) => {
      const done = () => {
        el.removeEventListener('ended', done)
        el.removeEventListener('error', done)
        resolve()
      }
      el.addEventListener('ended', done)
      el.addEventListener('error', done)
    })
  } catch {
    // Файлу немає або браузер заблокував — падаємо на синтез.
    if (opts.text) speak(opts.text, opts.rate)
  }
}

export function stop() {
  if (current) {
    current.pause()
    current = null
  }
  if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel()
}

/** Підвантажує наперед кілька файлів — щоб у сесії не було пауз. */
export function preload(keys: (string | undefined)[]) {
  for (const k of keys.slice(0, 12)) if (k) element(k).load()
}

// ── Запис власної вимови ──────────────────────────────────────────────────

export interface Recorder {
  stop: () => Promise<Blob>
  cancel: () => void
}

/** Чи вміє браузер записувати з мікрофона. */
export function canRecord(): boolean {
  return typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
}

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  // Safari підтримує mp4/aac, Chrome — webm/opus. Даємо браузеру обрати.
  const mime = ['audio/mp4', 'audio/webm'].find((m) => MediaRecorder.isTypeSupported(m))
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
  const chunks: BlobPart[] = []
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data)
  rec.start()

  const release = () => stream.getTracks().forEach((t) => t.stop())

  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        rec.onstop = () => {
          release()
          resolve(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }))
        }
        rec.stop()
      }),
    cancel: () => {
      try { rec.stop() } catch { /* уже зупинений */ }
      release()
    },
  }
}

/** Програє записаний користувачем фрагмент. */
export function playBlob(blob: Blob): HTMLAudioElement {
  const url = URL.createObjectURL(blob)
  const el = new Audio(url)
  el.addEventListener('ended', () => URL.revokeObjectURL(url), { once: true })
  void el.play()
  return el
}
